import { Box, Button, IconButton } from "@mui/material"
import React, { useEffect, useRef, forwardRef, Dispatch, SetStateAction } from "react"
import { useAuth } from "../hooks/useAuth"
import { useMixPanel } from "../hooks/useMixPanel"
import CloseIcon from "@mui/icons-material/Close";
import { T } from "../hooks/useTranslate"
import { PopupAndCardConfig, PopupAndCardState } from "@/types/components/popup"
import { usePopupContext } from "../hooks/PopupContext"

interface EventBriteCustomFormProps {
  data: PopupAndCardConfig
  handlePopupAttributeUpdate: (type: 'skip' | 'close') => Promise<void>
}
// Declare the global EBWidgets interface for TypeScript
declare global {
  interface Window {
    EBWidgets?: {
      createWidget: (config: {
        widgetType: string;
        eventId: string;
        iframeContainerId: string;
        iframeContainerHeight?: number;
        onOrderComplete?: () => void;
      }) => void;
    };
  }
}

interface PopupContextProperties {
  setPopupAndCardState: Dispatch<SetStateAction<PopupAndCardState>>;
}

const EventBriteCustomForm = forwardRef<Record<string, unknown>, EventBriteCustomFormProps>(({ data, handlePopupAttributeUpdate }, ref) => {
  // @ts-expect-error untyped auth value
  const { userInfo } = useAuth()
  const { logEvent } = useMixPanel()
  const widgetContainerRef = useRef<HTMLDivElement>(null)
  const isWidgetLoadedRef = useRef<boolean>(false)
  const { setPopupAndCardState } = usePopupContext() as PopupContextProperties

  // Load Eventbrite widget when modal opens
  useEffect(() => {
    // Prevent loading widget if it's already been loaded successfully
    if (isWidgetLoadedRef.current) {
      return
    }

    const eventbriteId = data?.eventDetails?.eventbriteId

    if (widgetContainerRef.current && eventbriteId) {
      const createEventbriteWidget = () => {
        if (window.EBWidgets && widgetContainerRef.current) {
          // Clear any existing widget content and create the container
          widgetContainerRef.current.innerHTML = `<div id="eventbrite-widget-container-${eventbriteId}"></div>`

          const onOrderComplete = async () => {
            logEvent('submit_eventbrite_form', {
              unicity_id: userInfo?.unicityId,
              country_code: userInfo?.countryCode,
              popup_content_id: data?.contentId,
            })
          }

          try {
            window.EBWidgets.createWidget({
              widgetType: 'checkout',
              eventId: eventbriteId,
              iframeContainerId: `eventbrite-widget-container-${eventbriteId}`,
              iframeContainerHeight: 425,
              onOrderComplete: onOrderComplete
            })
            // Mark widget as successfully loaded
            isWidgetLoadedRef.current = true
          } catch (error) {
            console.error('Error creating Eventbrite widget:', error)
          }
        } else {
          console.error('EBWidgets not available or container ref is null')
        }
      }

      // Load Eventbrite script if not already loaded
      if (!window.EBWidgets) {
        const script = document.createElement('script')
        script.src = 'https://www.eventbrite.com/static/widgets/eb_widgets.js'
        script.async = true
        script.onload = () => {
          logEvent('eventbrite_popup_loaded', {
            unicity_id: userInfo?.unicityId,
            country_code: userInfo?.countryCode,
            popup_content_id: data?.contentId,
            is_qualified: true
          })
          // Add a small delay to ensure the script is fully initialized
          setTimeout(() => {
            createEventbriteWidget()
          }, 100)
        }
        script.onerror = (error) => {
          console.error('Failed to load Eventbrite script:', error)
        }
        document.head.appendChild(script)
      } else {
        createEventbriteWidget()
      }
    }
     
  }, [userInfo?.unicityId, userInfo?.countryCode, data?.contentId, data?.eventDetails?.eventbriteId, logEvent])

  const handleCloseButtonClick = async () => {
    setPopupAndCardState(prev => {
      const events = prev.qualifiedEvents
      const index = events.findIndex(item => item.id === data.contentId)
      events[index].isCustomFormPopupActive = false

      return { ...prev, qualifiedEvents: events }
    })
    // if already submitted, don't do anything
    if (ref && typeof ref === 'object' && ref.current[`${data?.contentId}-submitted`]) return
    await handlePopupAttributeUpdate('skip')
  }

  return (
    <Box
      data-test-id="popup-modal-content"
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: {
          xs: '95vw',
          sm: '85vw',
          md: '700px',
        },
        minWidth: '300px',
        maxWidth: data?.styles?.maxWidth || '800px',
        maxHeight: {
          xs: '95vh',
          sm: '90vh',
        },
        overflow: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: '12px',
        outline: 'none',
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={() => handleCloseButtonClick()}
        size="small"
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        data-test-id="eventbrite-close-button"
        aria-label="close eventbrite"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Widget */}
      <Box sx={{ px: 2, pt: 1, pb: 3 }}>
        <div
          ref={widgetContainerRef}
          data-test-id="eventbrite-widget-container"
          style={{
            width: '100%',
            minHeight: '425px',
            border: 'none',
          }}
        />
        {
          ref && typeof ref === 'object' && ref.current[`${data?.contentId}-submitted`] && (
            <Button
              data-test-id="eventbrite-modal-close-button"
              onClick={() => handleCloseButtonClick()}
              sx={{
                mt: 2,
                borderRadius: '8px',
                backgroundColor: 'rgba(4, 158, 207, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(3, 130, 170, 1)',
                },
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                width: '100%',
                p: 1.5,
              }}
            >
              <T>close</T>
            </Button>
          )
        }
      </Box>
    </Box>
  )
})

EventBriteCustomForm.displayName = 'EventBriteCustomForm'

export default EventBriteCustomForm